import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Building2, ArrowLeft, Shield, Loader2, ShieldCheck, Globe, ExternalLink, Filter, Trash2, X } from 'lucide-react';
import { adminListBrands, adminDeleteBrand, adminChangePlan } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function AdminBrands() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [planFilter, setPlanFilter] = useState('');

  const load = async (plan) => {
    try {
      const data = await adminListBrands(plan);
      setItems(data);
    } catch { setItems([]); }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) { nav('/'); return; }
    load(planFilter);
  }, [user, authLoading, nav, planFilter]);

  return (
    <div className="py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-amber-700 flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Admin</div>
            <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-slate-900 flex items-center gap-2"><Building2 className="h-8 w-8 text-[#0d63b0]" /> Brand</h1>
          </div>
          <Button asChild variant="outline"><Link to="/admin"><ArrowLeft className="h-4 w-4 mr-1.5" /> Torna al pannello</Link></Button>
        </div>

        <div className="mb-4 flex items-center gap-2 text-sm">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-slate-600 mr-2">Filtra per piano:</span>
          {['', 'free', 'starter', 'pro'].map((p) => (
            <button key={p || 'all'} onClick={() => setPlanFilter(p)}
              className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${planFilter === p ? 'bg-[#0d63b0] text-white border-[#0d63b0]' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
              {p || 'Tutti'}
            </button>
          ))}
        </div>

        <Card className="border border-slate-200 p-0 overflow-hidden">
          {items === null && <div className="p-8 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>}
          {items && items.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">Nessun brand trovato.</div>}
          {items && items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">
                    <th className="px-5 py-3">Brand</th>
                    <th>Owner</th>
                    <th>Piano</th>
                    <th>Dominio custom</th>
                    <th>Push</th>
                    <th>Creato</th>
                    <th className="pr-5"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((b) => (
                    <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-5 py-3">
                        <div className="font-semibold text-slate-900">{b.name}</div>
                        <div className="text-[11px] font-mono text-slate-500">/b/{b.slug}</div>
                      </td>
                      <td className="text-slate-700">{b.owner_email || <span className="text-slate-400">—</span>}</td>
                      <td><span className="inline-flex rounded-full bg-blue-50 text-[#0d63b0] border border-blue-100 px-2 py-0.5 text-xs font-semibold capitalize">{b.plan}</span></td>
                      <td>
                        {b.custom_domain ? (
                          <span className="inline-flex items-center gap-1 font-mono text-xs">
                            {b.domain_verified ? <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> : <Globe className="h-3.5 w-3.5 text-amber-600" />}
                            <span className={b.domain_verified ? 'text-emerald-700' : 'text-amber-700'}>{b.custom_domain}</span>
                          </span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="text-slate-800 font-semibold">{b.total_pushes}</td>
                      <td className="text-slate-500 text-xs">{new Date(b.created_at).toLocaleDateString('it-IT')}</td>
                      <td className="pr-5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <select value={b.plan} onChange={async (e) => {
                              try { await adminChangePlan(b.id, e.target.value); toast.success('Piano aggiornato'); load(planFilter); }
                              catch { toast.error('Errore'); }
                            }} className="h-8 text-xs rounded border border-slate-200 px-1.5">
                            <option value="free">Free</option>
                            <option value="starter">Starter</option>
                            <option value="pro">Pro</option>
                          </select>
                          <Link to={`/b/${b.slug}`} target="_blank" className="p-2 text-slate-400 hover:text-[#0d63b0]" title="Visita"><ExternalLink className="h-3.5 w-3.5" /></Link>
                          <Button size="sm" variant="outline" title="Elimina brand" onClick={async () => {
                            if (!confirm(`Eliminare il brand "${b.name}" e tutti i suoi push? (l'utente owner NON viene eliminato)`)) return;
                            try { const r = await adminDeleteBrand(b.id); toast.success('Brand eliminato', { description: `${r.pushes_deleted || 0} push cancellati` }); load(planFilter); }
                            catch (e) { toast.error('Errore'); }
                          }} className="text-red-600 border-red-200 hover:border-red-300 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        {items && items.length > 0 && (
          <div className="mt-3 text-xs text-slate-500 text-right">{items.length} brand visualizzati</div>
        )}
      </div>
    </div>
  );
}
